import { store } from 'redux/store';
import { State } from 'redux/initialState';
import { useTranslate } from 'hooks/useTranslate';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { useMixpanel } from 'react-mixpanel-browser';

import { Board } from 'types';

import errorSwal from 'utils/errorSwal';
import trackEvent from 'utils/trackEvent';
import successSwal from 'utils/successSwal';

import { setModal } from 'modules/_shared/redux/modalActions';
import { updateBoard } from 'modules/boards/redux/boardActions';
import { basicBoardStatus } from 'modules/boards/constants/basicBoardStatus';
import { saveBasicBoard } from 'modules/boards/api/saveBasicBoard';
import { boardEntityTypes } from 'modules/_shared/components/BoardsListHeader/constants/boardEntityTypes';
import { boardVisibilityTypes } from 'modules/_shared/constants/boardVisibilityTypes';

import eventTypes from 'constants/eventTypes';
import actionTypes from 'constants/actionTypes';
import { DATE_FORMAT } from 'constants/defaultConstants';

import { generateBoardsName } from 'utils/generateBoardsName';
import { useGetStatusOptions } from '../hooks/useGetStatusOptions';
import { useGetAttendanceModes } from '../hooks/useGetAttendanceModes';
import { useGetVisibilityOptions } from '../hooks/useGetVisibilityOptions';
import { useGetAttendanceTypeOptions } from '../hooks/useGetAttendanceTypeOptions';
import { addBasicBoardModalInitValue } from '../constants/addBasicBoardModalInitValue';
import { AddBasicBoardModalContextType } from '../types/AddBasicBoardModalContextType';

const AddBasicBoardModalContext = createContext<AddBasicBoardModalContextType>(
  addBasicBoardModalInitValue
);

export const AddBasicBoardModalProvider: FC<{
  children: ReactNode;
  actionType: string;
  boardData?: Partial<Board>;
  size?: string;
}> = ({ children, actionType, boardData, ...props }) => {
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const mixpanel = useMixpanel();

  const actualSociety = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);

  const initialName =
    boardData?.name || generateBoardsName(boardEntityTypes.BASIC_BOARD);
  const initialDate = (boardData?.date as string) || new Date().toISOString();

  const {
    data: {
      selectedAttendanceType,
      selectedAttendanceTypeOption,
      attendanceTypeOptions,
    },
    actions: { handleChangeAttendanceType },
  } = useGetAttendanceTypeOptions({
    attendanceType: boardData?.attendanceType,
  });

  const {
    data: { selectedStatus, selectedStatusOption, statusOptions },
    actions: { handleChangeStatus },
  } = useGetStatusOptions({ status: boardData?.status });

  const {
    data: { selectedVisibility, selectedVisibilityOption, visibilityOptions },
    actions: { handleChangeVisibility },
  } = useGetVisibilityOptions({
    visibility: boardData?.isVisible
      ? boardVisibilityTypes.VISIBLE.value
      : boardVisibilityTypes.NOT_VISIBLE.value,
  });

  const {
    data: { place, onlineAccess },
    actions: { handleChangePlace, handleChangeOnlineAccess },
  } = useGetAttendanceModes({
    actualSociety,
    attendanceType: selectedAttendanceType,
    place: boardData?.place,
    onlineAccess: boardData?.onlineAccess,
  });

  const [name, setName] = useState<string>(initialName);
  const [date, setDate] = useState<string>(initialDate);
  const [comments, setComments] = useState<string>(boardData?.comments || '');
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleCloseModal = () => {
    dispatch(setModal(null));
  };

  const handleChangeName = (value: string) => {
    setName(value);
  };

  const handleChangeDate = (
    event: React.ChangeEvent<HTMLInputElement> // eslint-disable-line
  ) => {
    const newDate = event.target.value;
    setDate(newDate);

    const dateRegex = /\b(\d{1,2}\/\d{1,2}\/\d{4})\b/g;
    const dateFormatted = format(new Date(date), DATE_FORMAT);
    const updatedText = name.replace(dateRegex, dateFormatted);

    setName(updatedText);
  };

  const handleChangeComments = (value: string) => {
    setComments(value);
  };

  const handleChangeFiles = (newFiles: File[]) => {
    setFiles(newFiles);
  };

  const handleRemoveFile = (index: number) => {
    const updatedFiles = files.filter((_, i) => i !== index);
    setFiles(updatedFiles);
  };

  const handleAddBoard = async () => {
    try {
      const newBoardData = {
        name,
        user,
        files,
        date: new Date(date),
        society: actualSociety,
        attendanceType: selectedAttendanceType,
        place,
        onlineAccess,
        comments,
        status: selectedStatus,
        isVisible: false,
        entityType:
          boardEntityTypes.BASIC_BOARD as keyof typeof boardEntityTypes,
      };

      await saveBasicBoard(newBoardData);

      const currentEvent = eventTypes.ADD_BASIC_BOARD;
      trackEvent(mixpanel, currentEvent, {
        userId: user?.['_id'],
        userName: user?.name,
        userEmail: user?.email,
        societyId: actualSociety?.['_id'],
        societyName: actualSociety?.name,
        operation: currentEvent,
      });

      successSwal(t('BoardCreatedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorCreatingBoard'));
    }
  };

  const handleUpdateBoard = async () => {
    try {
      const updatedBoard = {
        ...boardData,
        name,
        date,
        attendanceType: selectedAttendanceType,
        place,
        onlineAccess,
        comments,
        status: selectedStatus,
        isVisible: !!(
          selectedVisibility === boardVisibilityTypes.VISIBLE.value
        ),
      };
      await store.dispatch(updateBoard(updatedBoard._id, updatedBoard, false));

      successSwal(t('BoardUpdatedSuccessfully'));
    } catch (error) {
      errorSwal(t('ErrorUpdatingBoard'));
    }
  };

  const handleSaveBasicBoard = async () => {
    setIsLoading(true);

    if (actionType === actionTypes.ADD) {
      await handleAddBoard();
    } else if (actionType === actionTypes.EDIT) {
      await handleUpdateBoard();
    }

    setIsLoading(false);
    handleCloseModal();
  };

  useEffect(() => {
    if (
      selectedStatus === basicBoardStatus.ACTIVE.value &&
      selectedVisibility !== boardVisibilityTypes.VISIBLE.value
    ) {
      const visibilityActiveOption = visibilityOptions.find(
        (option) => option.value === boardVisibilityTypes.VISIBLE.value
      );

      handleChangeVisibility(visibilityActiveOption || null);
    }
  }, [
    selectedStatus,
    selectedVisibility,
    visibilityOptions,
    handleChangeVisibility,
  ]);

  return (
    <AddBasicBoardModalContext.Provider
      value={{
        data: {
          name,
          date,
          selectedAttendanceType,
          selectedAttendanceTypeOption,
          attendanceTypeOptions,
          place,
          onlineAccess,
          comments,
          files,
          selectedStatus,
          selectedStatusOption,
          statusOptions,
          selectedVisibility,
          selectedVisibilityOption,
          visibilityOptions,
          actionType,
          isLoading,
        },
        actions: {
          handleCloseModal,
          handleChangeName,
          handleChangeDate,
          handleChangeAttendanceType,
          handleChangePlace,
          handleChangeOnlineAccess,
          handleChangeComments,
          handleChangeFiles,
          handleChangeStatus,
          handleChangeVisibility,
          handleRemoveFile,
          handleSaveBasicBoard,
        },
      }}
    >
      {children}
    </AddBasicBoardModalContext.Provider>
  );
};

export const useAddBasicBoardModal = () => {
  const context = useContext(AddBasicBoardModalContext);
  if (!context) {
    throw new Error(
      'useAddBasicBoardModal must be used within a AddBasicBoardModalProvider'
    );
  }
  return context;
};
